import { PlanCardType } from '@lib/sanity/queries/modules/pricingMain/types';
import { TextVariant } from '@ui/Text/constants';
import PortableText from '@ui/Text/PortableText';
import Text from '@ui/Text/Text';
import cn from '@utils/cn';
import { usePricingFrequency } from '../PricingMain/PricingFrequencyContext';
import PlanFrequencySwitcher from './PlanFrequencySwitcher';

type Props = {
  planCard: PlanCardType;
  isMobile?: boolean;
};

const PlanPrice = ({ planCard, isMobile = false }: Props) => {
  const { isYearlyFrequency } = usePricingFrequency();

  const calculatePrices = () => {
    const basePlanPrice = isYearlyFrequency ? planCard?.priceYearly : planCard?.priceMonthly;

    if (!basePlanPrice) {
      return { originalPrice: 0, discountedPrice: 0, hasDiscount: false };
    }

    const originalPrice = basePlanPrice;
    let hasDiscount = false;
    let discountedPrice = originalPrice;

    if (planCard.discount) {
      const { discount, discountFrequency } = planCard.discount;
      const applyDiscount =
        discountFrequency === 'both' ||
        (isYearlyFrequency && discountFrequency === 'yearly') ||
        (!isYearlyFrequency && discountFrequency === 'monthly');

      if (applyDiscount && discount) {
        hasDiscount = true;
        discountedPrice = basePlanPrice * (1 - discount / 100);
      }
    }

    return { originalPrice, discountedPrice, hasDiscount };
  };

  const { originalPrice, discountedPrice, hasDiscount } = calculatePrices();

  return (
    <>
      <div className={cn('flex w-full flex-col gap-2', isMobile ? 'items-center' : 'items-start')}>
        {planCard.customPricingLabel ? (
          <PortableText
            variant={TextVariant.TitleLG}
            content={planCard.customPricingLabel}
            className="h-[50px] font-medium"
          />
        ) : (
          <div className="flex w-full items-end justify-between">
            <div className="flex items-end gap-1">
              <div className="flex flex-wrap items-end justify-start gap-2 sm:flex-nowrap">
                <div className="flex items-end">
                  {hasDiscount && (
                    <div className="flex items-end">
                      <PortableText
                        content={planCard.currencySign}
                        variant={TextVariant.TitleSM}
                        className="font-medium text-[#9E9E9E] line-through"
                        style={{
                          color: hasDiscount
                            ? `rgba(${planCard.discount?.discountLabelColor?.rgb?.r},${planCard.discount?.discountLabelColor?.rgb?.g},${planCard.discount?.discountLabelColor?.rgb?.b},${planCard.discount?.discountLabelColor?.rgb?.a})`
                            : undefined,
                        }}
                      />
                      <Text
                        content={Math.round(originalPrice)?.toString() || '0'}
                        variant={TextVariant.TitleSM}
                        className="font-medium text-gray-400 line-through"
                        style={{
                          color: hasDiscount
                            ? `rgba(${planCard.discount?.discountLabelColor?.rgb?.r},${planCard.discount?.discountLabelColor?.rgb?.g},${planCard.discount?.discountLabelColor?.rgb?.b},${planCard.discount?.discountLabelColor?.rgb?.a})`
                            : undefined,
                        }}
                      />
                    </div>
                  )}
                  <PortableText
                    content={planCard.currencySign}
                    variant={TextVariant.TitleSM}
                    className={cn('font-medium')}
                  />
                  <Text
                    content={Math.round(hasDiscount ? discountedPrice : originalPrice)?.toString() || '0'}
                    variant={TextVariant.TitleSM}
                    className="font-medium"
                  />
                </div>
              </div>
              <PortableText
                content={isYearlyFrequency ? planCard.yearlyLabel : planCard.monthlyLabel}
                variant={TextVariant.TextBodySM}
                className="mb-1 sm:text-nowrap"
              />
            </div>
            <PlanFrequencySwitcher className="mb-1.5" planCard={planCard} />
          </div>
        )}
      </div>
      {/* Contact Us Link */}
      {planCard.contactUsLink?.url && (
        <div className="flex w-full items-center justify-center">
          <span onClick={() => window.open(planCard.contactUsLink?.url ?? '', '_blank')}>
            <PortableText
              variant={TextVariant.SubtitleMD}
              content={planCard.contactUsLink.label}
              className="cursor-pointer text-center font-medium underline"
            />
          </span>
        </div>
      )}
    </>
  );
};

export default PlanPrice;
